import { Row } from '@tanstack/react-table';
import { FieldPath, UseFormReturn } from 'react-hook-form';

import { hasBlockingQuickBooksWarnings } from '@/modules/finance/common';

import {
  GenerateBillListItem,
  GenerateBillMemberProjectListItem,
  GenerateBillMemberTimeOffListItem,
  GenerateBillSubRow,
  GenerateBillSubRowType,
} from '../../types';
import { GenerateBillsTableFormValues } from '../../validations';

/**
 * The generate-bills table is a two-level tree rendered through a single `GenerateBillListItem`
 * column model: depth 0 is the member row, depth 1 is any of its sub-rows (project, added
 * project, time-off, overhead, the derived salary adjustment, or the add-line button).
 *
 * TanStack types every row as the top-level model, so reading a sub-row's own shape needs a
 * cast. These helpers keep both the depth checks and those `as unknown as` casts in one place.
 */
export const isSubRow = (row: Row<GenerateBillListItem>) => row.depth === 1;

export const getSubRowType = (row: Row<GenerateBillListItem>): GenerateBillSubRowType =>
  (row.original as unknown as GenerateBillSubRow).type;

export const asSubRow = (row: Row<GenerateBillListItem>) =>
  row.original as unknown as GenerateBillSubRow;

const isSubRowOfType = (row: Row<GenerateBillListItem>, type: GenerateBillSubRowType) =>
  isSubRow(row) && getSubRowType(row) === type;

export const isProjectSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.PROJECT);
export const isTimeOffSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.TIME_OFF);
export const isOverheadSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.OVERHEAD);
export const isAddedProjectSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.ADDED_PROJECT);
export const isAddOverheadSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.ADD_OVERHEAD);
export const isSalaryAdjustmentSubRow = (row: Row<GenerateBillListItem>) =>
  isSubRowOfType(row, GenerateBillSubRowType.SALARY_ADJUSTMENT);

/** Rows that carry no billable figures of their own. */
export const isNonDataSubRow = (row: Row<GenerateBillListItem>) =>
  isAddOverheadSubRow(row) || isSalaryAdjustmentSubRow(row);

/**
 * Field names are keyed by STABLE ENTITY IDS, not array index, so edits survive filter
 * changes (a filter that re-shows an entity resolves the same path). Segments are prefixed
 * (`m`/`p`/`t`/`o`/`a`) so RHF treats them as object keys — a bare numeric id would be read as
 * an array index and land in the wrong (sparse) slot.
 */
export const getBillMemberPath = (memberId: number) => `member.m${memberId}` as const;

/**
 * An RHF field path into the bill form.
 *
 * The form schema is a `z.record` keyed by runtime entity ids, so a path assembled from those ids
 * can never be verified statically. Declaring it as RHF's own `FieldPath` — with the single
 * unavoidable cast confined to `getBillFieldName` below — lets every call site pass the result
 * straight to `control` / `watch` / `setValue` without casting again.
 */
export type BillFieldPath = FieldPath<GenerateBillsTableFormValues>;

export const getBillFieldName = (row: Row<GenerateBillListItem>, name: string): BillFieldPath => {
  const parentRow = row.getParentRow();
  // Rows with no parent (and unhandled sub-row types) have no schema path; a deliberately inert
  // one keeps callers from having to null-check every field lookup.
  if (!parentRow) return `__noop__.${name}` as BillFieldPath;

  const base = getBillMemberPath(parentRow.original.member.id);
  const subRow = asSubRow(row);
  const leaf = (() => {
    switch (subRow.type) {
      case GenerateBillSubRowType.TIME_OFF:
        return `${base}.timeOff.t${subRow.timeOffTypeId}.${name}`;
      case GenerateBillSubRowType.OVERHEAD:
        return `${base}.overhead.o${subRow.id}.${name}`;
      case GenerateBillSubRowType.ADDED_PROJECT:
        return `${base}.addedProject.a${subRow.id}.${name}`;
      case GenerateBillSubRowType.PROJECT:
        return `${base}.project.p${subRow.id}.${name}`;
      default:
        return `__noop__.${name}`;
    }
  })();

  return leaf as BillFieldPath;
};

/** Unselected rows and non-billable time-offs render their inputs read-only. */
export const isReadOnly = (row: Row<GenerateBillListItem>) => {
  if (!row.getIsSelected()) return true;
  if (isTimeOffSubRow(row)) {
    return !(row.original as unknown as GenerateBillMemberTimeOffListItem).isBillable;
  }
  return false;
};

export const hasMemberQuickBooksError = (row: Row<GenerateBillListItem> | undefined) =>
  row && hasBlockingQuickBooksWarnings(row.original.warnings);

/**
 * Only real project sub-rows carry a QuickBooks link, so only they can be missing one.
 *
 * This was previously written as `isSubRow && !isTimeOff && !isOverhead && !isAddedProject &&
 * !isAddOverhead && !hasQuickBooksIntegration`, which also matched SALARY_ADJUSTMENT rows —
 * and since that type has no `warnings` field, `hasBlockingQuickBooksWarnings(undefined)` would
 * have been safe, but stating the positive condition keeps the trap from resurfacing if that
 * ever changes.
 */
export const hasProjectQuickBooksError = (row: Row<GenerateBillListItem>) =>
  isProjectSubRow(row) &&
  hasBlockingQuickBooksWarnings(
    (row.original as unknown as GenerateBillMemberProjectListItem).warnings
  );

export const isRowDisabled = (row: Row<GenerateBillListItem>) =>
  isSubRow(row)
    ? hasMemberQuickBooksError(row.getParentRow()) || hasProjectQuickBooksError(row)
    : hasMemberQuickBooksError(row);

/** True when the member has at least one selectable sub-row. */
export const hasSelectableSubRows = (row: Row<GenerateBillListItem>) =>
  row.subRows.some(subRow => !isNonDataSubRow(subRow));

export const DISABLED_ROW_CLASS = 'opacity-50';

/**
 * Reading and writing a bill field.
 *
 * The record-keyed schema means RHF resolves every dynamic path's value type to a union of all
 * possible leaves, so each `setValue`/`getValues` would otherwise need its own cast. This facade
 * confines that unavoidable looseness to one place — cells should reach for it rather than casting
 * at each call site.
 */
export interface BillFormFieldAccessor {
  setFieldValue: (
    path: BillFieldPath,
    value: unknown,
    options?: { shouldValidate?: boolean; shouldDirty?: boolean }
  ) => void;
  getFieldValue: <TValue>(path: BillFieldPath) => TValue | undefined;
}

export const createBillFormFieldAccessor = (
  form: UseFormReturn<GenerateBillsTableFormValues>
): BillFormFieldAccessor => ({
  setFieldValue: (path, value, options) =>
    form.setValue(path, value as never, {
      shouldValidate: options?.shouldValidate ?? false,
      shouldDirty: options?.shouldDirty ?? false,
    }),
  getFieldValue: <TValue>(path: BillFieldPath) => form.getValues(path) as TValue | undefined,
});

/**
 * Row ids are built ONLY by the three functions below.
 *
 * TanStack derives them through `getBillRowId`, while the custom-row hooks have to predict the id
 * of a row that does not exist yet — they queue it so the row gets selected once the table data
 * rebuilds. Both sides must produce byte-identical ids: a literal that drifts here silently stops
 * selecting newly added rows rather than failing loudly.
 */
export const getBillMemberRowId = (memberId: number | string): string => `member-${memberId}`;

/** Id of a sub-row hanging off the member row `memberRowId`. */
export const getBillSubRowId = (memberRowId: string, subRow: GenerateBillSubRow): string => {
  switch (subRow.type) {
    case GenerateBillSubRowType.ADDED_PROJECT:
      return `${memberRowId}-added-project-${subRow.id}`;
    case GenerateBillSubRowType.TIME_OFF:
      return `${memberRowId}-timeoff-${subRow.timeOffTypeId}`;
    case GenerateBillSubRowType.OVERHEAD:
      return `${memberRowId}-overhead-${subRow.id}`;
    case GenerateBillSubRowType.ADD_OVERHEAD:
      return `${memberRowId}-add-overhead`;
    case GenerateBillSubRowType.SALARY_ADJUSTMENT:
      return `${memberRowId}-salary-adjustment`;
    case GenerateBillSubRowType.PROJECT:
      return `${memberRowId}-project-${subRow.id}`;
    default: {
      // Every sub-row type is spelled out above so a new one fails the build here rather than
      // silently inheriting another type's id shape.
      const _exhaustive: never = subRow;
      throw new Error(`Unhandled GenerateBillSubRowType: ${_exhaustive}`);
    }
  }
};

/**
 * Row identity for the table. Sub-rows are keyed by their own stable id (a UUID for user-added
 * rows) rather than array position, so selection and form paths survive re-ordering — and so the
 * same project/member can legitimately appear twice.
 */
export const getBillRowId = (
  row: GenerateBillListItem,
  _index: number,
  parent?: { id: string }
): string =>
  parent
    ? getBillSubRowId(parent.id, row as unknown as GenerateBillSubRow)
    : getBillMemberRowId(row.id);

/** The button row and the derived salary adjustment are not user-selectable. */
export const canSelectBillRow = (row: { original: GenerateBillListItem }): boolean => {
  const { type } = row.original as unknown as GenerateBillSubRow;
  return (
    type !== GenerateBillSubRowType.ADD_OVERHEAD &&
    type !== GenerateBillSubRowType.SALARY_ADJUSTMENT
  );
};
